/**
 * "Identidad de empresa" — branding row mirror of `mobile/src/store/senderInfo.ts`.
 *
 * Persistence lives in IndexedDB (same `cuadrilla-data` database, store
 * `branding` added in DB v3). The row is single-row, keyed by the
 * literal slug `'current'`, so the UI form can `getSenderInfo()` /
 * `saveSenderInfo()` without juggling identifiers.
 *
 * Sync (mobile↔web, web↔web of the same admin):
 *   `saveSenderInfo` stamps `updatedAt = Date.now()` and broadcasts a
 *   `branding_sync` blob via the team `sync` channel by default.
 *   Inbound messages route through `applyRemoteSenderInfo`, which
 *   writes the row directly without re-broadcasting (LWW comparison
 *   handled by the caller in `useLiveSync.ts`).
 *
 * Pub/sub:
 *   `subscribeSenderInfo` lets the form re-render when a remote
 *   change lands without a manual refresh — same shape as
 *   `subscribeStore` for the works pipeline.
 */
import type { WebIdentity } from './keys';
import { openDb } from './db';

const STORE_BRANDING = 'branding';
const ROW_ID = 'current';

export interface SenderInfo {
  /** Razón social o nombre comercial (always shown on PDFs). */
  companyName: string;
  /** CIF/NIF. Optional. */
  taxId?: string;
  /** Contact phone. Optional. */
  phone?: string;
  /** Contact email. Optional. */
  email?: string;
  /** Web / landing page. Optional. */
  web?: string;
  /** Mobile-only logo URI. Web never sets this; we surface a
   *  preview only when the value is a public URL the browser can
   *  render — `file://`/`content://` paths are filtered upstream
   *  in `mobile/src/sync/syncSenderInfo.ts`. */
  logoUri?: string;
  /** Epoch-ms last edit; LWW tiebreaker for `branding_sync`. */
  updatedAt?: number;
}

interface SenderInfoRow extends SenderInfo {
  /** Fixed slug — see `ROW_ID`. */
  id: string;
}

export interface SaveSenderInfoOptions {
  /** Opt out of broadcasting (used by the inbound sync handler so a
   *  remote-applied change doesn't echo back into the channel). */
  broadcast?: boolean;
}

// ── Pub/sub ─────────────────────────────────────────────────────────

type Listener = () => void;
const _listeners = new Set<Listener>();

function _emit(): void {
  for (const cb of _listeners) {
    try {
      cb();
    } catch {
      /* a single listener throwing must not break the chain */
    }
  }
}

/** Subscribe to "the branding row changed" — local OR remote. */
export function subscribeSenderInfo(listener: Listener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

// ── Read / write ────────────────────────────────────────────────────

export async function getSenderInfo(): Promise<SenderInfo | null> {
  const db = await openDb();
  return new Promise<SenderInfo | null>((resolve, reject) => {
    const tx = db.transaction(STORE_BRANDING, 'readonly');
    const req = tx.objectStore(STORE_BRANDING).get(ROW_ID);
    req.onsuccess = () => {
      const row = req.result as SenderInfoRow | undefined;
      if (!row) return resolve(null);
      // Strip the synthetic `id` so callers see the same shape mobile
      // produces — the slug is a storage detail, not part of the API.
      const { id: _id, ...rest } = row;
      void _id;
      resolve(rest);
    };
    req.onerror = () => reject(req.error ?? new Error('getSenderInfo failed'));
  });
}

async function _writeRow(info: SenderInfo): Promise<void> {
  const db = await openDb();
  const row: SenderInfoRow = { ...info, id: ROW_ID };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_BRANDING, 'readwrite');
    tx.objectStore(STORE_BRANDING).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('saveSenderInfo tx error'));
    tx.onabort = () => reject(tx.error ?? new Error('saveSenderInfo tx aborted'));
  });
}

/**
 * Persist locally and (by default) broadcast a `branding_sync` blob so
 * peer devices converge. The form's "Guardar" button is the primary
 * caller; the inbound sync dispatcher passes `{ broadcast: false }`
 * to avoid the obvious feedback loop.
 *
 * Identity is required when broadcasting because the encrypt step
 * needs the team key. Calls without identity fall back to local-only
 * persistence (e.g. degraded paths during a relay outage) and the
 * next successful broadcast resyncs peers.
 */
export async function saveSenderInfo(
  info: SenderInfo,
  identity: WebIdentity | null,
  opts: SaveSenderInfoOptions = {},
): Promise<SenderInfo> {
  const stamped: SenderInfo = {
    ...info,
    updatedAt: typeof info.updatedAt === 'number' ? info.updatedAt : Date.now(),
  };
  await _writeRow(stamped);
  _emit();

  if (opts.broadcast === false || !identity) return stamped;

  // Lazy import keeps the crypto/relay layer out of the eager module
  // graph — handy for unit tests that don't drive sync.
  try {
    const { broadcastSenderInfo } = await import('../sync/syncSenderInfo');
    await broadcastSenderInfo(stamped, identity);
  } catch {
    // Encrypt/send failure must never undo the local write.
  }
  return stamped;
}

/**
 * Apply an incoming `branding_sync` from the relay. LWW: the caller
 * (`useLiveSync`) compares timestamps and only invokes us when the
 * remote copy is newer than what we hold. Writes locally without
 * broadcasting (the message already came from the channel — echoing
 * it back would loop).
 */
export async function applyRemoteSenderInfo(info: SenderInfo): Promise<void> {
  await _writeRow(info);
  _emit();
}
